import React, { useState } from 'react';
import { Dialog, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  OnboardingStatus,
  onboardingStatus,
  TrayOnboardingStatus,
  trayOnboardingStatus,
} from 'api/Responses/SatoResponses';
import AsyncImage from 'components/AsyncImage/AsyncImage';
import BackButton from 'components/BackButton/BackButton';
import Page from 'components/Page';
import PageSpinner from 'components/PageSpinner/PageSpinner';
import { StatusTile } from 'components/StatusTile/StatusTile';
import { TableContainer } from 'components/Table/Table';
import { useLanguage } from 'context/AppContext';
import { useSelectedSet } from 'context/SelectedSet';
import useSatoSets from 'hooks/useSatoSets';
import { StorageType } from 'utils/helpers/image.helpers';
import { getTrayIndex } from 'utils/helpers/SATO.helpers';
import useReviewTray from './hooks/useReviewTray';
import RejectConfirmationModal from './molecules/RejectConfirmationModal';
import ReviewVideoModal from './molecules/ReviewVideoModal';
import * as Styles from './SATOVideosPage.styles';

const SATOVideosPage: React.FC = () => {
  const [language] = useLanguage();
  const navigate = useNavigate();
  const [rejecting, setRejecting] = useState(false);
  const { dispatchSelectedSet } = useSelectedSet();
  const { setCode = '', trayId = '' } = useParams<{ setCode: string; trayId: string }>();
  const { allDrafts } = useSatoSets();
  const trayIndex = getTrayIndex(allDrafts, setCode, trayId);
  const reviewTrayObject = useReviewTray(allDrafts, setCode, trayIndex);

  /**
   * This object may be undefined if this page is navigated-to directly.
   * Let's make sure it's defined before destructuring it!
   */
  if (!reviewTrayObject) return <></>;
  const { loading, set, tray, reviewTray } = reviewTrayObject;

  const handleCloseModal = (): void => setRejecting(false);

  const handleReject = async (comment: string): Promise<void> => {
    handleCloseModal();
    await reviewTray(TrayOnboardingStatus.REJECTED, comment);
  };

  const handleVideoClick = (videoIndex: number): void => {
    dispatchSelectedSet({
      type: 'SET_SELECTED_VIDEO',
      payload: videoIndex,
    });
  };

  return (
    <Page>
      <Styles.Container>
        <Styles.HeaderContainer>
          <Styles.TitleContainer>
            <BackButton onClick={() => navigate(`/sato/${set.code}`)} />
            <Styles.Title>Inside of {set.display_code}</Styles.Title>
          </Styles.TitleContainer>

          <Styles.StatusContainer>
            <Styles.Status>Tray Status:</Styles.Status>

            <StatusTile status={trayOnboardingStatus[tray.status]}>{language.Sato.status[tray.status]}</StatusTile>
          </Styles.StatusContainer>
        </Styles.HeaderContainer>

        <AsyncImage
          imgWidth={'30%'}
          imgHeight={'15rem'}
          src={tray.tray_image}
          alt={tray.tray_description}
          storageType={StorageType.FIREBASE}
        />
        <h2>{tray.tray_description}</h2>

        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{language.Sato.videosPage.category}</TableCell>
                <TableCell align="center">{language.Sato.videosPage.status}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {tray.videos.map((video, index) => (
                <TableRow key={`${video.type}_${video.order}`} onClick={() => handleVideoClick(index)}>
                  <TableCell>{language.Sato.videoType[video.type]}</TableCell>
                  <TableCell align="center">
                    <StatusTile status={onboardingStatus[video.status]}>
                      {language.Sato.status[video.status]}
                    </StatusTile>
                  </TableCell>
                </TableRow>
              ))}

              <TableRow className="default">
                <TableCell colSpan={2}>
                  <Styles.CommentsContainer>
                    <Styles.CommentTitle>{language.Sato.videosPage.comments}</Styles.CommentTitle>
                    {tray.comments.map(comment => (
                      <Styles.Comment key={comment}>{comment}</Styles.Comment>
                    ))}
                  </Styles.CommentsContainer>

                  <Styles.ButtonContainer>
                    <Styles.SATOButton onClick={() => setRejecting(true)}>
                      {language.Sato.videosPage.reject}
                    </Styles.SATOButton>
                    <Styles.SATOButton onClick={() => reviewTray(TrayOnboardingStatus.SKIPPED)}>
                      {language.Sato.videosPage.skip}
                    </Styles.SATOButton>
                    <Styles.SATOButton
                      onClick={() => reviewTray(TrayOnboardingStatus.MODEL_CONFIG)}
                      disabled={tray.videos.some(
                        video => ![OnboardingStatus.APPROVED, OnboardingStatus.REJECTED].includes(video.status),
                      )}
                    >
                      {language.Sato.videosPage.approve}
                    </Styles.SATOButton>
                  </Styles.ButtonContainer>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Styles.Container>

      <RejectConfirmationModal open={rejecting} onClose={handleCloseModal} onReject={handleReject} />

      <ReviewVideoModal />

      {/* Loading spinner */}
      <Dialog open={loading}>
        <PageSpinner />
      </Dialog>
    </Page>
  );
};

export default SATOVideosPage;
